import React from 'react'
import { useDispatch, batch } from 'react-redux'

import Title from '../../components/Title'
import MetaData from '../../components/MetaData'
import TrailMeta from '../../components/TrailMeta'
import ImageSlideshow from '../../components/ImageSlideshow'
import Loading from '../../components/Loading'
import Distance from '../../components/Distance'
import { DifficultText, DifficultIcon } from '../../components/Difficult'
import TravelTime from '../../components/TravelTime'
import Link from '../../components/Link'

import CrossHairs from '../../icons/crosshairs.svg'
import DistanceIcon from '../../icons/hiker.svg'
import TimeIcon from '../../icons/clock.svg'
import DownloadIcon from '../../icons/download.svg'
import AccommodationIcon from '../../icons/accommodation.svg'

import { settings } from '../../conf'
import { reverse, absoluteUrl } from '../../urls'
import { useIsMobile, showMapPanel, showExportPanel, useHaveInfoPanel } from '../../slices/ui'
import { setMapHighlightTrail, setMapExtent } from '../../slices/map'
import { useRoute, useRouteImage } from '../../api'
import { useUrlParams, useNavigatorOnline } from '../../hooks'
import { isTouchDevice } from '../../utils'
import {
  DOWNLOAD,
  GO_TO_STAGE,
  SHOW_ON_MAP,
  STAGE,
  STAGES_LIST,
  ROUTES_DESCRIPTION,
  TRAIL_DURATION,
  TRAIL_LENGTH,
  WHERE_TO_SLEEP
} from '../../trans'

const Trail = (props) => {

  const { route, trail, index } = props
  const dispatch = useDispatch()
  const isMobile = useIsMobile()

  const highlightTrail = () => dispatch(setMapHighlightTrail(trail.trail_id))
  const unHighlightTrail = () => dispatch(setMapHighlightTrail(null))
  const zoomToTrail = (e) => batch(() => {
    e.preventDefault()
    dispatch(showMapPanel(true))
    dispatch(setMapHighlightTrail(trail.trail_id))
    dispatch(setMapExtent({ bounds: trail.extent }))
  })
  const goToWhereToSleep = (e) => {

  }

  const whereToSleep = reverse(route.route_id, trail.trail_id, { type: 'trail', trail_id: trail.trail_id })

  return (
    <li className="pwa__list__item"
      onMouseEnter={(isMobile || isTouchDevice) ? undefined : highlightTrail}
      onMouseLeave={(isMobile || isTouchDevice) ? undefined : unHighlightTrail}>
      <Link to={{ pathname: reverse(route.route_id, index + 1), hash: '' }}
        className="fade-in block"
        title={ `${GO_TO_STAGE} ${trail.title}`}
        style={{ animationDelay: `${0.3 + 0.03 * index}s` }} >
        <div className="pwa__list__container">
          <h3 className="pwa__list__title inherit">
            <span className="pwa__list__subtitle block">
              {STAGE}&nbsp;{index + 1}
            </span>
            {trail.title}
          </h3>
          <ul className="pwa__list__labels">
            <li className="pwa__list__label">
              <DistanceIcon title={TRAIL_LENGTH}/>
              <Distance value={trail.length}/>
            </li>
            <li className="pwa__list__label">
              <TimeIcon title={TRAIL_DURATION}/>
              <TravelTime value={trail.time}/>
            </li>
            <li className="pwa__list__label">
              <DifficultIcon value={trail.difficult}/>&nbsp;
              <DifficultText value={trail.difficult}/>
            </li>
          </ul>
        </div>
      </Link>
      <div className="pwa__list__actions fade-in"
           style={{ animationDelay: `${0.3 + 0.03 * index}s` }}>
        <Link className="button circular pwa__list__action accommodations"
              to={whereToSleep}  
              title={WHERE_TO_SLEEP}
              onClick={goToWhereToSleep}>
            <AccommodationIcon title={WHERE_TO_SLEEP} />
        </Link>
        <button className="button circular pwa__list__action"
                title={SHOW_ON_MAP}
                onClick={ zoomToTrail }>
            <CrossHairs title={SHOW_ON_MAP}/>
        </button>

      </div>
    </li>
  )
}

const TrailList = React.memo((props) => {
  const { route } = props
  return route.trails.map(
    (trail, index) =>
      <Trail route={route} trail={trail} index={index} key={index} />
  )
})

const RouteMetaData = (props) => {
  const route = props.route
  const haveInfoPanel = useHaveInfoPanel() 
  if (!route || haveInfoPanel) return null
  const title = STAGES_LIST
  const description = ROUTES_DESCRIPTION({route_title:`${route.title} - ${route.subtitle}`})
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Trip",
    "name":  `${route.title} - ${route.subtitle}`,
    "description": route.description,    
    "offers": {
      "@type": "Offer",
      "price": "0"
    },
    "provider": {
      "@type": "Organization",
      "name": "Forwalk.org",
      "url": "https://forwalk.org",
      "logo": settings.STATIC_URL + 'images/logo_publisher.svg'
    },
     "subTrip": []
  }

  for (var trail of route.trails ){
    jsonLd['subTrip'].push({
        "@type": "TouristTrip",
        "name": `${trail.title} - ${trail.subtitle}`,
        "description": trail.description,
        "url": absoluteUrl(reverse(route.route_id, trail.trail_id))
    })
  }

  return <MetaData title={title} 
                   description={description} 
                   jsonLd={jsonLd} />
}

const RoutePage = (props) => {
  const dispatch = useDispatch()
  const { isOnline } = useNavigatorOnline()
  const { route_id } = useUrlParams(['route_id'])
  const route = useRoute({ routeId: route_id })
  const images = useRouteImage({ routeId: route_id })
  const imageItems = route.image ? [route.image] : images === undefined ? null : []

  if (images) {
    imageItems.push(...images)
  }

  if (!route) return (<Loading/>)

  return (
    <>
      <RouteMetaData route={route} /> 
      <ImageSlideshow images={imageItems}/>

      <Title title={route.title} subtitle={route.subtitle} className="options">
        <button className="button pwa__header__button" 
            disabled={!isOnline}
            onClick={() => dispatch(showExportPanel(true)) }>
            <DownloadIcon/>
            {DOWNLOAD}
        </button>
      </Title>


      <TrailMeta metadata={route} />

      <ol className="pwa__list steps">
        <TrailList route={route} />
      </ol>

    </>
  )
}

export default RoutePage
